<template>
  <!-- 移动端公共头部：左 logo，右侧 搜索 / 用户头像（区分登录态）/ 菜单（汉堡⇄X 变形）
       注：header 的 backdrop-filter 会使 fixed 子元素的包含块失效，抽屉与搜索面板需与 header 同级 -->
  <div class="h5-header-root">
    <header class="h5-header">
      <img class="h5-header__logo" :src="img('logo_header.png')" alt="Tushare" @click="goHome" />

      <div class="h5-header__actions">
        <!-- 搜索 -->
        <button class="h5-header__icon" type="button" aria-label="搜索" @click="openSearch">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
            <circle cx="11" cy="11" r="7" />
            <line x1="16.2" y1="16.2" x2="20.5" y2="20.5" />
          </svg>
        </button>

        <!-- 用户头像：未登录为线框人形（点击去登录，登录后回跳本页）；已登录展示头像并展开账号浮窗 -->
        <button class="h5-header__icon h5-header__icon--account" :class="{ 'h5-header__icon--open': accountMenuOpen }"
          type="button" aria-label="用户" aria-haspopup="true" :aria-expanded="accountMenuOpen ? 'true' : 'false'"
          @click="onAvatarClick">
          <img v-if="userInfo.id" class="h5-header__avatar" :src="avatarUrl" alt="用户头像" />
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
            <circle cx="12" cy="8" r="4" />
            <path d="M4.5 20.5c1.4-3.6 4.3-5.5 7.5-5.5s6.1 1.9 7.5 5.5" />
          </svg>
        </button>

        <!-- 菜单：三横线平滑变形为 X -->
        <button class="h5-header__icon h5-header__burger" :class="{ 'h5-header__burger--open': menuOpen }" type="button"
          aria-label="菜单" @click="toggleMenu">
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </header>

    <!-- 账号浮窗：个人中心 / 退出登录（与 PC 端下拉菜单一致）
         注：header 的 backdrop-filter 会使 fixed 子元素定位失效，故与 header 同级 -->
    <transition name="h5-account">
      <div v-if="accountMenuOpen" class="h5-account" role="menu" aria-label="账号菜单">
        <a class="h5-account__item" role="menuitem" href="javascript:void(0);" @click.stop="toUser">个人中心</a>
        <a class="h5-account__item h5-account__item--logout" role="menuitem" href="javascript:void(0);"
          :class="{ 'h5-account__item--loading': loggingOut }" @click.stop="logout">
          {{ loggingOut ? "退出中..." : "退出登录" }}
        </a>
      </div>
    </transition>

    <!-- 导航抽屉：与 PC 端导航栏完全对应的 7 项 -->
    <transition name="h5-menu">
      <div v-if="menuOpen" class="h5-menu">
        <nav class="h5-menu__nav">
          <a v-for="(item, i) in navItems" :key="item.label" class="h5-menu__item"
            :class="{ 'h5-menu__item--active': isActive(item) }" :style="{ '--i': i }"
            :href="item.href || 'javascript:void(0);'" @click="onItemClick(item)">
            <span>{{ item.label }}</span>
            <svg class="h5-menu__chev" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8"
              stroke-linecap="round" stroke-linejoin="round">
              <polyline points="6,3.5 10.5,8 6,12.5" />
            </svg>
          </a>
        </nav>
      </div>
    </transition>

    <!-- 移动端搜索面板（独立组件，搜索逻辑复用 PC 实现） -->
    <transition name="h5-search">
      <SearchPanel v-if="searchOpen" @close="closeSearch" />
    </transition>
  </div>
</template>

<script>
import SearchPanel from "./searchPanel.vue";
import api from "@/api/index";

const ASSETS = "home/";

export default {
  name: "H5Header",
  components: { SearchPanel },
  data() {
    return {
      menuOpen: false,
      searchOpen: false,
      // 登录后头像浮窗（个人中心 / 退出登录）
      accountMenuOpen: false,
      loggingOut: false,
      // 与 PC 端导航栏完全对应的导航项（首页指向移动端路由 /h5）
      navItems: [
        { label: "首页", route: "/h5" },
        { label: "平台介绍", href: "/document/1" },
        { label: "数据接口", href: "/document/2" },
        // { label: "资讯数据", href: "/news" },
        { label: "数据工具", href: "/webclient" },
        { label: "权限中心", route: "/permission" },
        { label: "活动套餐", route: "/combo" },
      ],
    };
  },
  computed: {
    userInfo() {
      return this.$store.state.userinfo;
    },
    // 头像：优先接口返回头像，缺省用站点官方默认头像（与 PC 用户模块一致）
    avatarUrl() {
      return this.userInfo.avatar || require("@/assets/images/user/header_icon.png");
    },
  },
  watch: {
    // 路由变化后收起抽屉、搜索面板与账号浮窗
    $route() {
      this.closeMenu();
      this.closeSearch();
      this.closeAccountMenu();
    },
  },
  mounted() {
    // 点击浮窗外部 / 按 Esc 收起账号浮窗
    // 用捕获阶段监听，避免页面内元素 stopPropagation 导致浮窗无法收起
    document.addEventListener("click", this.onDocumentClick, true);
    document.addEventListener("keydown", this.onDocumentKeydown);
  },
  methods: {
    img(name) {
      return require(`@/assets/images/${ASSETS}${name}`);
    },
    goHome() {
      if (this.$route.path !== "/h5") this.$router.push("/h5").catch(() => { });
    },
    isActive(item) {
      return !!item.route && this.$route.path.startsWith(item.route);
    },
    onItemClick(item) {
      if (item.href) return; // 站外/文档链接走默认跳转
      if (item.route && this.$route.path !== item.route) {
        this.$router.push(item.route).catch(() => { });
      }
      this.closeMenu();
    },
    // 头像点击：已登录展开账号浮窗（与 PC 端一致）；未登录仍走原登录跳转
    onAvatarClick() {
      if (!this.userInfo.id) {
        this.$router
          .push({ name: "login", query: { redirect: window.location.origin + "/#/h5" } })
          .catch(() => { });
        return;
      }
      this.toggleAccountMenu();
    },
    toggleAccountMenu() {
      if (this.accountMenuOpen) {
        this.closeAccountMenu();
        return;
      }
      // 与抽屉、搜索面板互斥，避免多层浮层叠加
      this.closeMenu();
      this.closeSearch();
      this.accountMenuOpen = true;
    },
    closeAccountMenu() {
      if (!this.accountMenuOpen) return;
      this.accountMenuOpen = false;
    },
    // 浮窗外部点击收起（头像按钮与浮窗自身的点击不处理）
    onDocumentClick(event) {
      if (!this.accountMenuOpen) return;
      const target = event.target;
      if (!target || !target.closest) return;
      if (target.closest(".h5-account") || target.closest(".h5-header__icon--account")) return;
      this.closeAccountMenu();
    },
    onDocumentKeydown(event) {
      if (event.key === "Escape") this.closeAccountMenu();
    },
    // 个人中心：与 PC 端一致，跳转 user 路由并收起浮窗
    toUser() {
      this.closeAccountMenu();
      this.$router.push({ name: "user" }).catch(() => { });
    },
    // 退出登录：与 PC 端 userInfo 逻辑一致（接口 → 清理用户信息 → 回首页）
    logout() {
      if (this.loggingOut) return;
      this.loggingOut = true;
      api
        .logout()
        .then(() => {
          this.$store.dispatch("clearUserinfo");
          this.closeAccountMenu();
          window.location.href = "/";
        })
        .finally(() => {
          this.loggingOut = false;
        });
    },
    toggleMenu() {
      if (this.menuOpen) {
        this.closeMenu();
      } else {
        this.closeSearch();
        this.closeAccountMenu();
        this.menuOpen = true;
        this.lockBody(true);
      }
    },
    closeMenu() {
      if (!this.menuOpen) return;
      this.menuOpen = false;
      this.lockBody(false);
    },
    openSearch() {
      if (this.searchOpen) return;
      this.closeMenu();
      this.closeAccountMenu();
      this.searchOpen = true;
      this.lockBody(true);
    },
    closeSearch() {
      if (!this.searchOpen) return;
      this.searchOpen = false;
      this.lockBody(false);
    },
    // 面板展开期间锁定背景滚动（移动端全屏面板惯例）
    lockBody(lock) {
      document.body.style.overflow = lock ? "hidden" : "";
    },
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick, true);
    document.removeEventListener("keydown", this.onDocumentKeydown);
    this.lockBody(false);
  },
};
</script>

<style lang="scss" scoped>
$font: "Source Han Sans SC";
$brand: #3461ff;
$text-6: #1d2129;
$text-5: #4e5969;
$text-4: #86909c;
$fill-5: #e5e6eb;

.h5-header {
  display: flex;
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  height: 64px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  z-index: 30;
  font-family: $font;

  &,
  & * {
    box-sizing: border-box;
  }

  &__logo {
    width: 96px;
    height: 48px;
    cursor: pointer;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  /* 图标按钮：40x40 触控热区，细线图标风格与 PC 描边体系一致 */
  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    outline: none;
    background: transparent;
    padding: 0;
    width: 40px;
    height: 40px;
    color: $text-6;
    cursor: pointer;
    transition: color 0.25s ease, transform 0.15s ease;

    svg {
      width: 22px;
      height: 22px;
    }

    &:active {
      color: $brand;
      transform: scale(0.92);
    }
  }

  &__avatar {
    border: 1px solid $fill-5;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    object-fit: cover;
  }

  /* 汉堡⇄X：上下两线旋转收拢、中线淡出，0.3s 标准缓动 */
  &__burger {
    flex-direction: column;
    gap: 5px;

    span {
      display: block;
      border-radius: 1px;
      background: currentColor;
      width: 20px;
      height: 2px;
      transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease;
    }

    &--open {
      span:nth-child(1) {
        transform: translateY(7px) rotate(45deg);
      }

      span:nth-child(2) {
        opacity: 0;
        transform: scaleX(0.3);
      }

      span:nth-child(3) {
        transform: translateY(-7px) rotate(-45deg);
      }
    }
  }
}

/* 头像按钮展开态：与浮窗呼应 */
.h5-header__icon--open {
  color: $brand;
}

/* ===================== 账号浮窗（个人中心 / 退出登录） =====================
   Figma 5652:4895：110x96、r8、白底、阴影 0 2 8 rgba(0,0,0,.08)；
   两行各 40 高、文字 14/22 居中、上下留白 8 */
.h5-account {
  position: fixed;
  top: 64px;
  right: 8px;
  z-index: 29;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 8px 0;
  width: 110px;
  font-family: $font;

  &,
  & * {
    box-sizing: border-box;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    line-height: 22px;
    letter-spacing: 0;
    color: $text-6;
    font-size: 14px;
    font-weight: 400;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.2s ease, color 0.25s ease;

    &:active {
      background: #f2f3f5;
      color: $brand;
    }
  }

  &__item--loading {
    opacity: 0.6;
    pointer-events: none;
  }
}

/* 展开/收起：淡入 + 上浮微位移（与站点 0.25s ease 动效节奏一致） */
.h5-account-enter-active,
.h5-account-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.h5-account-enter,
.h5-account-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
  transform-origin: top right;
}

@media (prefers-reduced-motion: reduce) {

  .h5-account-enter-active,
  .h5-account-leave-active {
    transition: none;
  }
}

/* ===================== 导航抽屉 ===================== */
.h5-menu {
  position: fixed;
  top: 64px;
  right: 0;
  bottom: 0;
  left: 0;
  background: #ffffff;
  overflow-y: auto;
  font-family: $font;
  z-index: 25;

  &,
  & * {
    box-sizing: border-box;
  }

  &__nav {
    display: flex;
    flex-direction: column;
    padding: 8px 16px 24px;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #f2f3f5;
    padding: 16px 0;
    line-height: 24px;
    letter-spacing: 0;
    color: $text-6;
    font-size: 16px;
    text-decoration: none;
    cursor: pointer;
    transition: color 0.25s ease;

    &--active {
      color: $brand;
      font-weight: 500;
    }

    &:active {
      color: $brand;
    }
  }

  &__chev {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: $text-4;
  }
}

/* 抽屉容器过渡：淡入 + 下沉微位移（0.25s，与站点动效节奏一致） */
.h5-menu-enter-active,
.h5-menu-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.h5-menu-enter,
.h5-menu-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* 导航项逐条错峰入场（每条延迟 40ms）；收起时立即整体淡出，不再错峰 */
.h5-menu-enter-active .h5-menu__item {
  transition: opacity 0.25s ease, transform 0.25s ease;
  transition-delay: calc(var(--i) * 40ms);
}

.h5-menu-enter .h5-menu__item {
  opacity: 0;
  transform: translateY(10px);
}

/* 搜索面板过渡：同款淡入 + 微位移 */
.h5-search-enter-active,
.h5-search-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.h5-search-enter,
.h5-search-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
